import { decodeLandingSeed } from "../lib/compact-landing-seed.mjs";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  getGetDailyLandingInventoryQueryOptions,
} from "@workspace/api-client-react";
import type {
  DailyLandingInventory,
  DailyLandingInventoryCohort,
  GetDailyLandingInventoryParams,
  PublicSearchCarCard,
} from "@workspace/api-zod";
import type { CarCardData } from "@/lib/api";
import { marketToCountry } from "@/i18n/markets";

const DAY_MS = 24 * 60 * 60 * 1000;
const GENERATION_BUILD_POLL_MS = 5 * 60 * 1000;

function pathKey(path: string): string {
  const stripped = path.replace(/^\/+|\/+$/g, "");
  return stripped ? `/${stripped}` : "/";
}

function countryFromPath(path: string): string | undefined {
  const market = pathKey(path).split("/").filter(Boolean)[0];
  return market ? marketToCountry(market)?.toUpperCase() : undefined;
}

export function dailyLandingInventoryQueryKey(params: GetDailyLandingInventoryParams) {
  return [
    "/api/landing-pages/fixed-inventory",
    pathKey(params.path),
    params.country_code?.toUpperCase() || null,
    params.locale?.toLowerCase().split("-")[0] || null,
  ] as const;
}

export function matchesDailyLandingInventoryScope(
  inventory: DailyLandingInventory | undefined,
  params: GetDailyLandingInventoryParams,
): inventory is DailyLandingInventory {
  // SSR/cache payloads are external evidence, even when statically typed.
  // A missing path must be rejected, not coerced to "/" or used in replace().
  if (!inventory || typeof inventory.path !== "string" || typeof params.path !== "string" ||
      pathKey(inventory.path) !== pathKey(params.path)) return false;
  const expectedCountry = params.country_code?.toUpperCase() || countryFromPath(params.path);
  if (expectedCountry && inventory.countryCode?.toUpperCase() !== expectedCountry) return false;
  // An unscoped root URL cannot safely reuse a country-bound SSR generation.
  if (!expectedCountry && inventory.countryCode) return false;
  const expectedLocale = params.locale?.toLowerCase().split("-")[0];
  if (expectedLocale &&
    inventory.locale?.toLowerCase().split("-")[0] !== expectedLocale) return false;
  return true;
}

function readPrerenderSeed(
  params: GetDailyLandingInventoryParams,
): DailyLandingInventory | undefined {
  if (typeof document === "undefined") return undefined;
  const node = document.getElementById("landing-prerender-data");
  if (!node?.textContent) return undefined;
  try {
    const parsed = decodeLandingSeed(JSON.parse(node.textContent)) as {
      path?: unknown;
      inventoryBrand?: unknown;
      fixedInventory?: DailyLandingInventory | null;
    };
    const path = typeof parsed.path === "string" ? parsed.path : "";
    return pathKey(path) === pathKey(params.path) &&
      matchesDailyLandingInventoryScope(parsed.fixedInventory ?? undefined, params)
      ? parsed.fixedInventory ?? undefined
      : undefined;
  } catch {
    return undefined;
  }
}

export function dailyLandingInitialDataUpdatedAt(inventory: DailyLandingInventory): number {
  const generatedAt = Date.parse(inventory.generatedAt || "");
  if (Number.isFinite(generatedAt)) return generatedAt;
  const nextRefreshAt = Date.parse(inventory.nextRefreshAt || "");
  return Number.isFinite(nextRefreshAt) ? nextRefreshAt - DAY_MS : 0;
}

export function dailyLandingStaleTime(
  inventory: DailyLandingInventory | undefined,
  dataUpdatedAt: number,
): number {
  const nextRefreshAt = Date.parse(inventory?.nextRefreshAt || "");
  return Number.isFinite(nextRefreshAt)
    ? Math.max(0, nextRefreshAt - dataUpdatedAt)
    : DAY_MS;
}

export function dailyLandingRefetchInterval(
  inventory: DailyLandingInventory | undefined,
  now: number,
): number | false {
  const refreshAt = Date.parse(inventory?.nextRefreshAt || "");
  if (!Number.isFinite(refreshAt)) return false;
  const untilRefresh = refreshAt - now;
  return untilRefresh > 0
    ? Math.max(1_000, untilRefresh + 1_000)
    : GENERATION_BUILD_POLL_MS;
}

function dailyLandingQueryStaleTime(currentQuery: {
  state: { data: unknown; dataUpdatedAt: number };
}): number {
  return dailyLandingStaleTime(
    currentQuery.state.data as DailyLandingInventory | undefined,
    currentQuery.state.dataUpdatedAt,
  );
}

function dailyLandingQueryRefetchInterval(currentQuery: {
  state: { data: unknown };
}): number | false {
  return dailyLandingRefetchInterval(
    currentQuery.state.data as DailyLandingInventory | undefined,
    Date.now(),
  );
}

export function mapDailyLandingCar(car: PublicSearchCarCard): CarCardData {
  return {
    ...car,
    city: car.city ?? "",
    state: car.state ?? "",
    images: car.images?.map((image) => ({
      ...image,
      url: image.url ?? null,
    })),
    priceDriving: car.priceDriving ?? null,
    priceEvent: car.priceEvent ?? null,
    chauffeurMinPrice: car.chauffeurMinPrice ?? null,
    eventMinPrice: car.eventMinPrice ?? null,
    eventHourlyRate: car.eventHourlyRate ?? null,
    chauffeuredHourlyRate: car.chauffeuredHourlyRate ?? null,
    minimumPrice: car.minimumPrice ?? null,
    primaryImageUrl: car.primaryImageUrl ?? null,
    primaryImageCdnThumbnail: car.primaryImageCdnThumbnail ?? null,
    primaryImageCdnMedium: car.primaryImageCdnMedium ?? null,
    primaryImageCdnFull: car.primaryImageCdnFull ?? null,
    rentalDrivingActive: car.rentalDrivingActive ?? false,
    rentalChauffeurActive: car.rentalChauffeurActive ?? false,
    rentalEventActive: car.rentalEventActive ?? false,
    totalScore: car.totalScore ?? null,
    reviewsRating: car.reviewsRating ?? null,
    reviewsCount: car.reviewsCount ?? null,
    transmission: car.transmission ?? null,
    passengers: car.passengers ?? null,
    latitude: car.latitude ?? null,
    longitude: car.longitude ?? null,
  };
}

export function getDailyLandingCohort(
  inventory: DailyLandingInventory | undefined,
  cohortKey?: string,
): DailyLandingInventoryCohort | undefined {
  if (inventory?.state !== "ready") return undefined;
  const key = cohortKey || inventory.defaultCohort;
  return inventory.cohorts.find((cohort) => cohort.key === key);
}

export function keepLastGoodDailyLandingInventory(
  previous: DailyLandingInventory | undefined,
  next: DailyLandingInventory | undefined,
  params: GetDailyLandingInventoryParams,
  requiredCohortKeys: string[] = [],
): DailyLandingInventory | undefined {
  const keys = requiredCohortKeys.length
    ? requiredCohortKeys
    : [next?.defaultCohort || previous?.defaultCohort].filter((key): key is string => !!key);
  const hasRequiredCohorts = (inventory: DailyLandingInventory | undefined) =>
    inventory?.state === "ready" &&
    keys.every((key) => getDailyLandingCohort(inventory, key) !== undefined);
  return hasRequiredCohorts(previous) &&
    matchesDailyLandingInventoryScope(previous, params) &&
    (!hasRequiredCohorts(next) || !matchesDailyLandingInventoryScope(next, params))
    ? previous
    : next;
}

/**
 * Reads the immutable landing generation. SSR seeds retain their source
 * timestamp. A complete SSR seed stays bound to its published HTML generation
 * until navigation; API outages and a later inventory-only activation cannot
 * replace its cars while the matching complete HTML is still being built.
 */
export function useDailyLandingInventory(
  params: GetDailyLandingInventoryParams,
  options: {
    enabled?: boolean;
    initialData?: DailyLandingInventory | null;
    cohortKey?: string;
    requiredCohortKeys?: string[];
  } = {},
) {
  // Parsing the document on every render creates a new cohort identity and
  // makes consumers report unchanged cars back into their parent indefinitely.
  const candidateSeed = useMemo(
    () => options.initialData ?? readPrerenderSeed(params),
    [options.initialData, params.path, params.country_code, params.locale],
  );
  const scopedSeed = matchesDailyLandingInventoryScope(candidateSeed ?? undefined, params)
    ? candidateSeed ?? undefined
    : undefined;
  const inventoryQueryKey = dailyLandingInventoryQueryKey(params);
  const query = useQuery(getGetDailyLandingInventoryQueryOptions(params, {
    query: {
      // Brand is retained on the request as compatibility/display metadata,
      // but it must not split the frozen stock cache.
      queryKey: inventoryQueryKey,
      enabled: options.enabled !== false && scopedSeed?.state !== "ready",
      initialDataUpdatedAt: scopedSeed
        ? dailyLandingInitialDataUpdatedAt(scopedSeed)
        : undefined,
      staleTime: dailyLandingQueryStaleTime,
      refetchOnWindowFocus: scopedSeed?.state !== "ready",
      refetchOnMount: scopedSeed?.state !== "ready",
      refetchInterval: scopedSeed?.state === "ready" ? false : dailyLandingQueryRefetchInterval,
      refetchIntervalInBackground: scopedSeed?.state !== "ready",
      structuralSharing: (previous, next) => keepLastGoodDailyLandingInventory(
        previous as DailyLandingInventory | undefined,
        next as DailyLandingInventory | undefined,
        params,
        options.requiredCohortKeys || (options.cohortKey ? [options.cohortKey] : []),
      ),
      initialData: scopedSeed,
      retry: 1,
    },
  }));
  // React Query can already contain another generation for this route. A
  // validated HTML seed, not that shared cache, owns this document's cohort.
  const candidateInventory = (scopedSeed?.state === "ready" ? scopedSeed : query.data) as
    DailyLandingInventory | undefined;
  const scopeMatches = matchesDailyLandingInventoryScope(candidateInventory, params);
  const inventory = scopeMatches ? candidateInventory : undefined;
  const cohort = getDailyLandingCohort(inventory, options.cohortKey);
  const cars = useMemo(() => cohort?.cars.map(mapDailyLandingCar) ?? [], [cohort?.cars]);

  return {
    ...query,
    inventory,
    cohort,
    cars,
    scopeMatches,
    scopeInvalid: candidateInventory !== undefined && !scopeMatches,
  };
}
